/**
 * settings-search-index.ts
 *
 * Contextual search index for the Sovereign Mesh Settings & Control Center.
 * Provides instant fuzzy and keyword matching across all settings sections, tabs, and actions.
 */

export interface SettingsSearchItem {
  id: string;
  tabId: 'identity' | 'members' | 'topology' | 'compute' | 'vault' | 'modules' | 'security' | 'commands' | 'observability';
  title: string;
  description: string;
  keywords: readonly string[];
}

export const SETTINGS_SEARCH_CATALOG: readonly SettingsSearchItem[] = [
  {
    id: 'identity-profile',
    tabId: 'identity',
    title: 'Profile & Display Name',
    description: 'Edit your display name, HNS alias, bio, and public identity',
    keywords: ['profile', 'name', 'alias', 'hns', 'handle', 'bio', 'identity'],
  },
  {
    id: 'identity-public-key',
    tabId: 'identity',
    title: 'Ed25519 Public Key',
    description: 'View and copy your master cryptographic public key',
    keywords: ['key', 'public key', 'ed25519', 'crypto', 'signature', 'spki'],
  },
  {
    id: 'identity-avatar',
    tabId: 'identity',
    title: 'Avatar & Profile Photo',
    description: 'Upload, crop, and configure high-resolution avatars',
    keywords: ['avatar', 'photo', 'picture', 'image', 'crop', 'icon'],
  },
  {
    id: 'identity-theme',
    tabId: 'identity',
    title: 'Workspace Theme & Colors',
    description: 'Switch between Neon Dark, Titanium Light, Midnight Blue, and Cyberpunk presets',
    keywords: ['theme', 'color', 'dark mode', 'light mode', 'design', 'preset', 'appearance'],
  },
  {
    id: 'compute-invite',
    tabId: 'compute',
    title: 'Helper Node Enrollment Invitation',
    description: 'Generate single-use invitation URIs to link worker nodes (MacBook Air, PC, etc.)',
    keywords: ['invite', 'invitation', 'helper', 'worker', 'enroll', 'link', 'macbook air', 'node', 'setup string'],
  },
  {
    id: 'compute-models',
    tabId: 'compute',
    title: 'Local AI Models & Needle GGUF',
    description: 'Manage local LLMs, Needle 2 schema extractor, and Metal GPU acceleration',
    keywords: ['model', 'ai', 'llm', 'gguf', 'needle', 'gpu', 'metal', 'inference'],
  },
  {
    id: 'compute-leases',
    tabId: 'compute',
    title: 'Admin Capacity Leases',
    description: 'Approve and monitor compute/storage resource grants from helper nodes',
    keywords: ['lease', 'capacity', 'grant', 'worker', 'fencing', 'compute'],
  },
  {
    id: 'security-passkey',
    tabId: 'security',
    title: 'WebAuthn Passkeys & Security Keys',
    description: 'Manage FIDO2 passkeys, Touch ID, Windows Hello, and hardware tokens',
    keywords: ['passkey', 'webauthn', 'fido2', 'touch id', 'windows hello', 'yubikey', 'biometric', 'security'],
  },
  {
    id: 'security-recovery',
    tabId: 'security',
    title: 'Emergency Recovery Codes & TOTP',
    description: 'Generate KMS-peppered single-use recovery codes and configure authenticator 2FA',
    keywords: ['recovery', 'recovery code', 'totp', '2fa', 'authenticator', 'break glass', 'emergency'],
  },
  {
    id: 'security-backup',
    tabId: 'security',
    title: 'Sovereign Encrypted Backup',
    description: 'Export and restore encrypted sovereign vault backups',
    keywords: ['backup', 'export', 'restore', 'encrypted', 'json', 'vault'],
  },
  {
    id: 'security-audit',
    tabId: 'security',
    title: 'Security Audit Trail',
    description: 'Export ISO-compliant notification and security event logs',
    keywords: ['audit', 'trail', 'log', 'security', 'iso', 'event'],
  },
  {
    id: 'members-streaming',
    tabId: 'members',
    title: 'Broadcasting & Video Streaming Policy',
    description: 'Control whether non-admin peers are permitted to stream video',
    keywords: ['stream', 'streaming', 'video', 'broadcast', 'policy', 'members'],
  },
  {
    id: 'members-guest-pass',
    tabId: 'members',
    title: 'Guest Passes & Temporary Access',
    description: 'Issue short-lived guest access tokens for visitors',
    keywords: ['guest', 'pass', 'token', 'visitor', 'access', 'members'],
  },
  {
    id: 'topology-cluster',
    tabId: 'topology',
    title: 'Mesh Network Topology & Relays',
    description: 'Inspect active cluster peers, routing hops, and HNS record tables',
    keywords: ['topology', 'network', 'mesh', 'relay', 'hop', 'peers', 'cluster'],
  },
  {
    id: 'modules-plugins',
    tabId: 'modules',
    title: '18 Canonical Microkernel Plugins',
    description: 'Enable or disable modular extensions (CRM, Invoicing, Kanban, Chat, Whiteboard)',
    keywords: ['plugin', 'module', 'extension', 'crm', 'invoicing', 'kanban', 'chat', 'whiteboard'],
  },
  {
    id: 'observability-telemetry',
    tabId: 'observability',
    title: 'Subsystem Health & Telemetry',
    description: 'Inspect live memory, event loop latency, and subsystem health logs',
    keywords: ['observability', 'telemetry', 'health', 'metrics', 'latency', 'subsystems'],
  },
];

export function searchSettings(query: string): SettingsSearchItem[] {
  if (!query || !query.trim()) return [];
  const normalized = query.trim().toLowerCase();

  return SETTINGS_SEARCH_CATALOG.filter((item) => {
    if (item.title.toLowerCase().includes(normalized)) return true;
    if (item.description.toLowerCase().includes(normalized)) return true;
    if (item.keywords.some((k) => k.toLowerCase().includes(normalized))) return true;
    return false;
  });
}
